import { defineStore } from "pinia";

import type { Sprache } from "@/models/Sprache";
import type { Vermittlungsstelle } from "@/models/Vermittlungsstelle";
import type { Kanton } from "@/models/Kanton";
import type { Betriebszweig } from "@/models/Betriebszweig";
import type { Bewirtschaftungsart } from "@/models/Bewirtschaftungsart";
import type { Lage } from "@/models/Lage";
import type { Nebenbetrieb } from "@/models/Nebenbetrieb";
import type { Tier } from "@/models/Tier";
import type { Geschlecht } from "@/models/Geschlecht";
import type { Land } from "@/models/Land";
import type { Ernaehrungsform } from "@/models/Ernaehrungsform";
import type { Sprachkenntnis } from "@/models/Sprachkenntnis";
import type { Partnerschulen } from "@/models/Partnerschulen";
import type { Aufenthaltsbewilligungstypen } from "@/models/Aufenthaltbewilligungstypen";
import AuswahlwerteService from "@/services/AuswahlwerteService";
import type { Angebotsstatus } from "@/models/Angebotsstatus";
import type { Belegungsstatus } from "@/models/Belegungsstatus";
import type { Einsatzbereich } from "@/models/Einsatzbereich";
import type { Sanitaereeinrichtung } from "@/models/SanitaereEinrichtung";
import type { Einsatzort } from "@/models/Einsatzort";
import type { EinsatzherkunftJugendliche } from "@/models/EinsatzherkunftJugendliche";

// Define the shape of the auswahlwerteMap
interface AuswahlwerteState {
  betriebszweige: Betriebszweig[] | [];
  bewirtschaftungsarten: Bewirtschaftungsart[] | [];
  hoflage: Lage[] | [];
  nebenbetriebe: Nebenbetrieb[] | [];
  tiere: Tier[] | [];
  vermittlungsstellen: Vermittlungsstelle[] | [];
  geschlechter: Geschlecht[] | [];
  sprachen: Sprache[] | [];
  subventionskantone: Kanton[] | [];
  laender: Land[] | [];
  laenderAll: Land[] | [];
  ernaehrungsformen: Ernaehrungsform[] | [];
  sprachkenntnisse: Sprachkenntnis[] | [];
  partnerschulen: Partnerschulen[] | [];
  aufenthaltsbewilligungstypen: Aufenthaltsbewilligungstypen[] | [];
  einsatzbereiche: Einsatzbereich[] | [];
  angebotstati: Angebotsstatus[] | [];
  belegungsstati: Belegungsstatus[] | [];
  sanitaereeinrichtungen: Sanitaereeinrichtung[] | [];
  einsatzorte: Einsatzort[] | [];
  einsatzherkunftJugendliche: EinsatzherkunftJugendliche[] | [];
  loading: boolean;
  error: string | null;
}

export const useAuswahlwerteStore = defineStore("auswahlwerte", {
  state: (): AuswahlwerteState => ({
    betriebszweige: [],
    bewirtschaftungsarten: [],
    hoflage: [],
    nebenbetriebe: [],
    tiere: [],
    vermittlungsstellen: [],
    geschlechter: [],
    sprachen: [],
    subventionskantone: [],
    laender: [],
    laenderAll: [],
    ernaehrungsformen: [],
    sprachkenntnisse: [],
    partnerschulen: [],
    aufenthaltsbewilligungstypen: [],
    einsatzbereiche: [],
    angebotstati: [],
    belegungsstati: [],
    sanitaereeinrichtungen: [],
    einsatzorte: [],
    einsatzherkunftJugendliche: [],
    loading: false,
    error: null,
  }),

  actions: {
    async fetchAuswahlwerte() {
      await AuswahlwerteService.fetchAuswahlwerte(); // Fetches auswahlwerte from the server
    },

    clearAuswahlwerte() {
      this.$reset(); // Resets state to initial values
    },
  },

  getters: {
    isDataLoaded: (state) => {
      // Consider data loaded if all required arrays have at least one item
      return [
        state.betriebszweige,
        state.bewirtschaftungsarten,
        state.hoflage,
        state.nebenbetriebe,
        state.tiere,
        state.vermittlungsstellen,
        state.geschlechter,
        state.sprachen,
        state.subventionskantone,
        state.laender,
        state.laenderAll,
        state.ernaehrungsformen,
        state.sprachkenntnisse,
        state.partnerschulen,
        state.aufenthaltsbewilligungstypen,
        state.einsatzbereiche,
        state.angebotstati,
        state.belegungsstati,
        state.sanitaereeinrichtungen,
        state.einsatzorte,
        state.einsatzherkunftJugendliche,
      ].every((arr) => Array.isArray(arr) && arr.length > 0);
    },
  },
});
